import { Drawer, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { S3BackupSegment } from "../../api/types";
import { getS3BackupSegments } from "../../api/backups";
import { useAuth } from "../../providers/AuthProvider";

type SegmentsDrawerProps = {
  configId: string | null;
  configName: string;
  onClose: () => void;
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const SegmentsDrawer = ({
  configId,
  configName,
  onClose,
}: SegmentsDrawerProps) => {
  const { token } = useAuth();
  const [segments, setSegments] = useState<S3BackupSegment[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (configId === null) return;
    setLoading(true);
    getS3BackupSegments(token!, configId).then((res) => {
      setSegments(res?.segments ?? []);
      setLoading(false);
    });
  }, [configId]);

  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (name: string) => (
        <Typography.Text code style={{ fontSize: 11 }}>
          {name}
        </Typography.Text>
      ),
    },
    {
      title: "App ID",
      dataIndex: "appId",
      key: "appId",
      render: (appId: string | null) => appId ?? <em>all</em>,
    },
    {
      title: "Start",
      dataIndex: "startDate",
      key: "startDate",
      render: (d: string) => new Date(d).toLocaleString(),
    },
    {
      title: "End",
      dataIndex: "endDate",
      key: "endDate",
      render: (d: string) => new Date(d).toLocaleString(),
    },
    {
      title: "Size",
      dataIndex: "fileSizeBytes",
      key: "fileSizeBytes",
      render: (bytes: number) => formatBytes(bytes),
    },
    {
      title: "Docs",
      dataIndex: "documentCount",
      key: "documentCount",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "uploaded" ? "green" : "default"}>{status}</Tag>
      ),
    },
    {
      title: "Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d: string) => new Date(d).toLocaleString(),
    },
  ];

  return (
    <Drawer
      title={`Segments — ${configName}`}
      open={configId !== null}
      onClose={onClose}
      width={900}
    >
      <Table
        dataSource={segments}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="small"
        scroll={{ x: true }}
      />
    </Drawer>
  );
};
